import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Collapse, CircularProgress } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { NumericFormat } from "react-number-format";
import { useParams } from "react-router-dom";
import {
	CustomizedDivider,
	CustomizedLink,
	CustomizedText,
} from "../../../components/styled-componnets";
import {
	CalculatorContainer,
	DiscountContainer,
	PriceAndValueContainer,
	PriceContainer,
	ExpandMore,
	TotalOwnershipContainer,
	SuperBuiltUpAreaContainer,
} from "../styled-components";
import { EventProps } from "../../../utils/interface";
import Colors from "../../../utils/theme/color";
import { UNICODE, USER_ROLES, VALIDATION_REGEXPS } from "../../../constants";
import { EMPTY_LIST_PRICE_DETAILS } from "../../../utils/booking-utils";
import { ListPriceDataType } from "../../../components/list-price/list-price-interface";
import { useGetUserDetailsHooks } from "../../../hooks";
import MaterialModal from "../../../components/modal";
import DiscountOrRoundOffInput from "./discount-or-round-off-input";
import { useGetProposalDetailsQuery } from "../../../../features/api/proposals-api-slice";
import { PROPOSAL_DETAILS_PROPS } from "../../../utils/proposals-utils";

const totalDiscountContainerProps = {
	backgroundColor: Colors.lightGreen,
	display: "flex",
	justifyContent: "center",
	border: ` 1px solid ${Colors.greenColor}`,
	padding: 1,
	borderRadius: 1,
};

const strickenPriceStyleProps = {
	textDecoration: "line-through",
	marginTop: 8,
	marginRight: 3,
	color: Colors.spanishGray,
};

const materialModalProps = {
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
};

interface PriceCalculatorProps {
	isAddDiscountPopupVisible: boolean;
	isUpdateProposalLoading: boolean;
	toggleAddDiscountPopUp: () => void;
	maximumDiscountOffered: {
		max_discount_by_sales_executive: number;
		max_discount_by_sales_head: number;
		max_discount_by_sales_lead: number;
		max_discount_by_channel_partner: number;
	};
	updateProposalDetails: (actionTriggered: boolean, payload: {}) => void;
	toggleRoundOffModal: () => void;
	isEditable: boolean;
	blockOtherActions: (errorMessage: string) => void;
	project?: string;
}

const DISCOUNT_MAX_LENGTH = 3;

const PriceCalculator: FC<PriceCalculatorProps> = ({
	isAddDiscountPopupVisible,
	toggleAddDiscountPopUp,
	isUpdateProposalLoading,
	maximumDiscountOffered,
	updateProposalDetails,
	toggleRoundOffModal,
	isEditable,
	blockOtherActions,
	project,
}) => {
	const { t } = useTranslation();
	const {
		isProjectSalesUser,
		isProjectSalesHead,
		isProjectHead,
		isCurrentUserCP,
		highestUserRole,
		salesHead,
		salesLead,
	} = useGetUserDetailsHooks();

	const [discountDetails, setDiscountDetails] = useState<ListPriceDataType>(
		EMPTY_LIST_PRICE_DETAILS
	);

	// CHANGE 1: REMOVED the problematic `useRef`
	// const discountDetailsRef = useRef(discountDetails);

	const [isBasicCostExpandable, setIsBasicCostExpandable] = useState(true);
	const [isAdditionalCostExpandable, setIsAdditionalCostExpandable] =
		useState(true);
	const [isTaxesCostExpandable, setIsTaxesCostExpandable] = useState(true);
	const [isMiscellaneousCostExpandable, setIsMiscellaneousCostExpandable] =
		useState(true);

	const [discountAdded, setDiscountAdded] = useState("");
	const [discountErrorMessage, setDiscountErrorMessage] = useState("");

	const params = useParams();
	const { getUserToken } = useGetUserDetailsHooks();
	const { proposalId } = params || {};

	// CHANGE 2: Destructure `isLoading` to handle the loading state
	const { data: getProposalDetailResponse, refetch: refetchProposalDetails, isLoading } =
		useGetProposalDetailsQuery(
			{
				userToken: getUserToken(),
				proposalId,
			},
			{ skip: !Boolean(proposalId) }
		);

	const getPriceDetails = useCallback(
		(details: PROPOSAL_DETAILS_PROPS) => {
			console.log('API Details:', details);
			console.log('Private Garden Area from API:', details?.private_garden_area);
			console.log('Private Garden Area type:', typeof details?.private_garden_area);
			const mappedData = {
				discountBySalesExecutive: details?.discount_by_sales_executive || 0,
				discountBySalesLead: details?.discount_by_sales_lead || 0,
				discountBySalesHead: details?.discount_by_sales_head || 0,
				discountByChannelPartner: details?.discount_by_channel_partner || 0,
				totalDiscount: details?.discount_offered || 0,
				saleableArea: details?.apartment_area || 0,
				bookingBasicAmount: details?.booking_basic_amount || 0,
				bookingTotalConsideration: details?.booking_total_consideration || 0,
				basicRate: details?.basic_rate || 0,
				premiumLocationCharges: details?.premium_location_charges || 0,
				bookingFloorPremium: details?.booking_floor_premium || 0,
				floorPremium: details?.floor_premium || 0,
				plc: details?.premium_location_charges || 0,
				additionalExpenses: details?.additional_expenses || 0,
				coveredCarParking: details?.covered_car_parking || 0,
				clubHouse: details?.club_house || 0,
				gstAmount: details?.booking_gst_amount || 0,
				gstPercentage: details?.booking_gst_percentage || 0,
				totalConsiderationIncludingGst:
					details?.total_consideration_including_gst || 0,
				sinkingFund: details?.sinking_fund || 0,
				maintenanceCharges: details?.maintenance_charges || 0,
				legalCharges: details?.legal_charges || 0,
				BookingEKhata: details?.booking_e_katha_amount || 0,
				totalCostOfOwnership: details?.total_cost_of_ownership || 0,
				ratePerSquareFeet: details?.rate_per_square_feet || 0,
				bookingRatePerSquareFeet: details?.booking_rate_per_square_feet || 0,
				basicAmount: details?.basic_amount || 0,
				totalAmount: details?.total_amount || 0,
				roundOff: details?.round_off || 0,
				discount: details?.total_discount || 0,
				totalEffectiveCostBeforeRoundOff: details?.booking_total_amount || 0,
				effectiveCostBeforeRoundOff: details?.effective_cost_per_square_feet || 0,
				totalConsideration: details?.total_consideration || 0,
				bookingTotalConsiderationIncludingGst:
					details?.booking_total_consideration_including_gst || 0,
				bookingPrivateGardenCost: details?.booking_private_garden_cost || 0,
				bookingPrivateGardenArea: details?.private_garden_area || 0,
			};
			console.log('Mapped Private Garden Area:', mappedData.bookingPrivateGardenArea);
			return mappedData;
		},
		[]
	);

	const totalDiscount = useMemo(
		() =>
			(discountDetails?.discountBySalesExecutive || 0) +
			(discountDetails?.discountBySalesLead || 0) +
			(discountDetails?.discountBySalesHead || 0) +
			(discountDetails?.discountByChannelPartner || 0),
		[
			discountDetails?.discountBySalesExecutive,
			discountDetails?.discountBySalesHead,
			discountDetails?.discountBySalesLead,
			discountDetails?.discountByChannelPartner,
		]
	);

	const additionalDetails = useMemo(
		() =>
			(discountDetails?.additionalExpenses || 0) +
			(discountDetails?.coveredCarParking || 0) +
			(discountDetails?.clubHouse || 0),
		[
			discountDetails?.additionalExpenses,
			discountDetails?.clubHouse,
			discountDetails?.coveredCarParking,
		]
	);

	const effectiveRate = useMemo(
		() => (discountDetails.ratePerSquareFeet || 0) - totalDiscount,
		[discountDetails.ratePerSquareFeet, totalDiscount]
	);

	const totalEffectiveRate = useMemo(
		() => effectiveRate * (discountDetails?.saleableArea || 0),
		[discountDetails?.saleableArea, effectiveRate]
	);

	const additionalCost = useMemo(
		() => totalEffectiveRate + additionalDetails,
		[additionalDetails, totalEffectiveRate]
	);

	const gstPercentage = useMemo(
		() => (discountDetails?.gstPercentage || 0) / 100,
		[discountDetails?.gstPercentage]
	);

	const gstAmount = useMemo(
		() => additionalCost * gstPercentage,
		[additionalCost, gstPercentage]
	);

	const considerationIncludingGst = useMemo(
		() => gstAmount + additionalCost,
		[additionalCost, gstAmount]
	);

	const miscellaneousCost = useMemo(
		() =>
			(discountDetails?.legalCharges || 0) +
			(discountDetails?.maintenanceCharges || 0) +
			(discountDetails?.sinkingFund || 0),
		[
			discountDetails?.legalCharges,
			discountDetails?.maintenanceCharges,
			discountDetails?.sinkingFund,
		]
	);

	const totalCostOfOwnership = useMemo(
		() =>
			(
				considerationIncludingGst +
				miscellaneousCost -
				(discountDetails?.roundOff || 0)
			).toFixed(),
		[considerationIncludingGst, discountDetails?.roundOff, miscellaneousCost]
	);

	// CHANGE 3: `useMemo` now correctly depends on state, not the deleted ref.
	const discount = useMemo(
		() => (discountDetails?.basicAmount || 0) - totalEffectiveRate,
		[discountDetails?.basicAmount, totalEffectiveRate]
	);

	const calculateDiscounts = useCallback(() => {
		// This function can remain as is, since it reads from the memos which in turn read from state
		setDiscountDetails((prevDetails) => ({
			...prevDetails,
			totalDiscount: totalDiscount,
			bookingRatePerSquareFeet: effectiveRate,
			bookingBasicAmount: totalEffectiveRate,
			bookingTotalConsideration: additionalCost,
			gstAmount: gstAmount,
			bookingTotalConsiderationIncludingGst: considerationIncludingGst,
			totalCostOfOwnership: totalCostOfOwnership,
			effectiveCostBeforeRoundOff: (
				(totalEffectiveRate - (prevDetails?.roundOff || 0)) /
				(prevDetails?.saleableArea || 1) // Avoid division by zero
			).toFixed(),
			totalEffectiveCostBeforeRoundOff:
				considerationIncludingGst + miscellaneousCost,
			discount,
		}));
	}, [
		additionalCost,
		considerationIncludingGst,
		effectiveRate,
		gstAmount,
		miscellaneousCost,
		totalCostOfOwnership,
		totalDiscount,
		totalEffectiveRate,
		discount,
	]);

	// CHANGE 4: REMOVED the useEffect that was updating the ref. It's no longer needed.
	// useEffect(() => {
	//   if (!Object.is(discountDetails, EMPTY_LIST_PRICE_DETAILS)) {
	//     discountDetailsRef.current = discountDetails;
	//   }
	// }, [discountDetails]);


	useEffect(() => {
		refetchProposalDetails();
	}, [refetchProposalDetails]);

	useEffect(() => {
		if (isAddDiscountPopupVisible) {
			setDiscountErrorMessage("");
		}
	}, [isAddDiscountPopupVisible]);

	useEffect(() => {
		blockOtherActions(discountErrorMessage);
	}, [blockOtherActions, discountErrorMessage]);

	// CHANGE 5: SIMPLIFIED and safer useEffect for populating and calculating data.
	useEffect(() => {
		// This condition populates the state once the API call is successful.
		if (!discountAdded && getProposalDetailResponse?.data?.[0]) {
			setDiscountDetails(getPriceDetails(getProposalDetailResponse.data[0]));
		}

		// This condition runs the calculations whenever the user is actively typing a discount.
		if (discountAdded) {
			calculateDiscounts();
		}
	}, [
		discountAdded,
		getPriceDetails,
		getProposalDetailResponse?.data,
		calculateDiscounts,
	]);

	// ... (All other functions from onChangeAmount to validateDiscountAndClose remain the same)
	// ... They correctly use `setDiscountDetails` so they will trigger re-renders.

	const onClickViewDetails = (id: string) => {
		if (id === t("list_price.basic_apartment_cost")) {
			setIsBasicCostExpandable(!isBasicCostExpandable);
		} else if (id === t("list_price.additional_cost")) {
			setIsAdditionalCostExpandable(!isAdditionalCostExpandable);
		} else if (id === t("list_price.taxes")) {
			setIsTaxesCostExpandable(!isTaxesCostExpandable);
		} else {
			setIsMiscellaneousCostExpandable(!isMiscellaneousCostExpandable);
		}
	};

	const expandBasedOnId = (id: string) =>
		id === t("list_price.basic_apartment_cost")
			? isBasicCostExpandable
			: id === t("list_price.additional_cost")
				? isAdditionalCostExpandable
				: id === t("list_price.taxes")
					? isTaxesCostExpandable
					: isMiscellaneousCostExpandable;


	const onChangeAmount = (e: EventProps) => {
		setDiscountErrorMessage("");
		setDiscountAdded(
			e.target.value.replace(VALIDATION_REGEXPS.matchesAnyNumbers, "")
		);
		setDiscountDetails({
			...discountDetails,
			discountByChannelPartner:
				highestUserRole === USER_ROLES.ChannelPartner
					? parseInt(
						e.target.value.replace(VALIDATION_REGEXPS.matchesAnyNumbers, "")
					)
					: discountDetails?.discountByChannelPartner,
			discountBySalesExecutive:
				highestUserRole === USER_ROLES.ProjectSalesUser
					? parseInt(
						e.target.value.replace(VALIDATION_REGEXPS.matchesAnyNumbers, "")
					)
					: discountDetails?.discountBySalesExecutive,
			discountBySalesLead:
				highestUserRole === salesLead
					? parseInt(
						e.target.value.replace(VALIDATION_REGEXPS.matchesAnyNumbers, "")
					)
					: discountDetails?.discountBySalesLead,
			discountBySalesHead:
				highestUserRole === salesHead
					? parseInt(
						e.target.value.replace(VALIDATION_REGEXPS.matchesAnyNumbers, "")
					)
					: discountDetails?.discountBySalesHead,
		});
	};

	const discountInvalidMessage = useCallback(
		(limit: number) =>
			setDiscountErrorMessage(t("list_price.discount_limit_error", { limit })),
		[t]
	);

	const updateDiscount = useCallback(() => {
		setDiscountAdded("");
		const actionTriggered = false;
		const payload = {
			name: getProposalDetailResponse?.data[0]?.name,
			opportunity: getProposalDetailResponse?.data[0]?.opportunity,
			project: getProposalDetailResponse?.data[0]?.project,
			unit: getProposalDetailResponse?.data[0]?.unit,
			customer_ask_discount:
				getProposalDetailResponse?.data[0]?.customer_ask_discount,
			discount_by_sales_executive: discountDetails?.discountBySalesExecutive,
			discount_by_sales_lead: discountDetails?.discountBySalesLead,
			discount_by_sales_head: discountDetails?.discountBySalesHead,
			discount_by_channel_partner: discountDetails?.discountByChannelPartner,
			round_off: discountDetails?.roundOff,
			same_whatsapp: getProposalDetailResponse?.data[0]?.same_whatsapp,
			whatsapp_no: getProposalDetailResponse?.data[0]?.whatsapp_no,
		};
		updateProposalDetails(actionTriggered, payload);
	}, [
		discountDetails?.discountBySalesExecutive,
		discountDetails?.discountBySalesHead,
		discountDetails?.discountBySalesLead,
		discountDetails?.discountByChannelPartner,
		discountDetails?.roundOff,
		getProposalDetailResponse?.data,
		updateProposalDetails,
	]);


	const maximumDiscountForSalesLead = useMemo(
		() =>
			maximumDiscountOffered?.max_discount_by_sales_lead +
			maximumDiscountOffered?.max_discount_by_sales_executive,
		[
			maximumDiscountOffered?.max_discount_by_sales_executive,
			maximumDiscountOffered?.max_discount_by_sales_lead,
		]
	);

	const maximumDiscountForSalesHead = useMemo(
		() =>
			maximumDiscountOffered?.max_discount_by_sales_head +
			maximumDiscountOffered?.max_discount_by_sales_lead +
			maximumDiscountOffered?.max_discount_by_sales_executive,
		[
			maximumDiscountOffered?.max_discount_by_sales_executive,
			maximumDiscountOffered?.max_discount_by_sales_head,
			maximumDiscountOffered?.max_discount_by_sales_lead,
		]
	);

	const isChannelPartnerDiscountValid = useCallback(
		(discount?: string) =>
			discount &&
			parseInt(discount) <=
			maximumDiscountOffered?.max_discount_by_channel_partner,
		[maximumDiscountOffered?.max_discount_by_channel_partner]
	);

	const isSalesUserDiscountValid = useCallback(
		(discount?: string) =>
			discount &&
			parseInt(discount) <=
			maximumDiscountOffered?.max_discount_by_sales_executive,
		[maximumDiscountOffered?.max_discount_by_sales_executive]
	);

	const isSalesLeadDiscountValid = useCallback(
		(discount?: string) =>
			discount && parseInt(discount) <= maximumDiscountForSalesLead,
		[maximumDiscountForSalesLead]
	);

	const isSalesHeadDiscountValid = useCallback(
		(discount?: string) =>
			discount && parseInt(discount) <= maximumDiscountForSalesHead,
		[maximumDiscountForSalesHead]
	);

	const saveAndUpdateDiscount = useCallback(
		(discount: string) => {
			// Check highest role first - users can have multiple roles
			if (isProjectHead) {
				if (isSalesHeadDiscountValid(discount)) {
					updateDiscount();
				} else {
					discountInvalidMessage(maximumDiscountForSalesHead);
				}
			} else if (isProjectSalesHead) {
				if (isSalesLeadDiscountValid(discount)) {
					updateDiscount();
				} else {
					discountInvalidMessage(maximumDiscountForSalesLead);
				}
			} else if (isProjectSalesUser) {
				if (isSalesUserDiscountValid(discount)) {
					updateDiscount();
				} else {
					discountInvalidMessage(
						maximumDiscountOffered?.max_discount_by_sales_executive
					);
				}
			} else if (isCurrentUserCP) {
				if (isChannelPartnerDiscountValid(discount)) {
					updateDiscount();
				} else {
					discountInvalidMessage(
						maximumDiscountOffered?.max_discount_by_channel_partner
					);
				}
			} else {
				setDiscountErrorMessage("");
			}
		},
		[
			discountInvalidMessage,
			isProjectHead,
			isCurrentUserCP,
			isProjectSalesHead,
			isProjectSalesUser,
			isSalesHeadDiscountValid,
			isSalesLeadDiscountValid,
			isSalesUserDiscountValid,
			isChannelPartnerDiscountValid,
			maximumDiscountForSalesHead,
			maximumDiscountForSalesLead,
			maximumDiscountOffered?.max_discount_by_channel_partner,
			maximumDiscountOffered?.max_discount_by_sales_executive,
			updateDiscount,
		]
	);

	const onClickSaveDiscount = useCallback(() => {
		saveAndUpdateDiscount(discountAdded);
	}, [discountAdded, saveAndUpdateDiscount]);

	const validateDiscountAndClose = (discount?: string) => {
		toggleAddDiscountPopUp();
		if (Boolean(discount)) {
			// Check highest role first - users can have multiple roles
			if (isProjectHead) {
				if (!isSalesHeadDiscountValid(discount)) {
					discountInvalidMessage(maximumDiscountForSalesHead);
				}
			} else if (isProjectSalesHead) {
				if (!isSalesLeadDiscountValid(discount)) {
					discountInvalidMessage(maximumDiscountForSalesLead);
				}
			} else if (isProjectSalesUser) {
				if (!isSalesUserDiscountValid(discount)) {
					discountInvalidMessage(
						maximumDiscountOffered?.max_discount_by_sales_executive
					);
				}
			} else if (isCurrentUserCP) {
				if (!isChannelPartnerDiscountValid(discount)) {
					discountInvalidMessage(maximumDiscountOffered?.max_discount_by_channel_partner);
				}
			} else {
				setDiscountErrorMessage("");
			}
		}
	};

	// CHANGE 6: All rendering functions now use `discountDetails` (state) instead of `discountDetailsRef.current`
	const renderSuperBuiltUpArea = () => (
		<SuperBuiltUpAreaContainer>
			<CustomizedText fontSize={20} font={"Lato-Regular"}>
				{t("list_price.super_built_up_area")}
			</CustomizedText>
			<CustomizedText
				fontSize={20}
				sx={{ fontWeight: "700" }}
				font={"Lato-Regular"}
			>
				{t("list_price.square_feet_area", {
					squareFeetValue: discountDetails?.saleableArea,
				})}
			</CustomizedText>
		</SuperBuiltUpAreaContainer>
	);

	const renderAdditionalCostDetails = () => (
		<Box sx={{ width: "100%" }}>
			{renderPriceAndValue({
				label: t("list_price.additional_expense"),
				value: discountDetails?.additionalExpenses,
			})}
			{renderPriceAndValue({
				label: t("list_price.covered_car_parking"),
				value: discountDetails?.coveredCarParking,
			})}
			{renderPriceAndValue({
				label: t("list_price.club_house"),
				value: discountDetails?.clubHouse,
			})}
			{project === "Bren Ananta" && renderPriceAndValue({
				label: t("list_price.booking_private_garden_cost"),
				value: discountDetails?.bookingPrivateGardenCost,
			})}
		</Box>
	);

	// ... and so on for ALL rendering functions. I have updated them below.

	// NOTE: ALL functions below this line have been checked to ensure they use `discountDetails` from state.

	const renderLinkToViewDetails = ({ id }: { id: string }) => (
		<Box>
			<CustomizedLink onClick={() => onClickViewDetails(id)}>
				{t("list_price.view_details")}
			</CustomizedLink>
			<ExpandMore
				expand={expandBasedOnId(id)}
				onClick={() => onClickViewDetails(id)}
				aria-expanded={expandBasedOnId(id)}
				aria-label="show more"
			>
				<ExpandMoreIcon sx={{ color: Colors.linkColor }} />
			</ExpandMore>
		</Box>
	);

	const renderAmountInCurrencyFormat = ({
		price = 0,
		fontColor = Colors.titleColor,
		isFontBold = false,
		isTextStrikeThrough = false,
		fontSize = 18,
		textAlign = "right",
	}: {
		price?: number | string;
		fontColor?: string;
		isFontBold?: boolean;
		isTextStrikeThrough?: boolean;
		fontSize?: number;
		textAlign?: any;
	}) => (
		<NumericFormat
			style={{
				...{
					textAlign: textAlign,
					fontWeight: isFontBold ? "700" : "200",
					fontFamily: "Lato-Regular",
					fontSize: fontSize,
					color: fontColor ?? Colors.titleColor,
				},
				...(isTextStrikeThrough && strickenPriceStyleProps),
			}}
			value={price || 0} // Ensure value is not undefined/null
			allowLeadingZeros
			thousandSeparator=","
			prefix={`${UNICODE.rupeeSymbol}`}
			displayType="text"
			thousandsGroupStyle="lakh"
		/>
	);

	const renderPriceAndValue = ({
		label,
		value,
		isFontBold = false,
		actualValue,
	}: {
		label: string;
		value: any;
		isFontBold?: boolean;
		actualValue?: any;
	}) => {
		return (
			<PriceAndValueContainer>
				<CustomizedText
					fontSize={18}
					sx={{ fontWeight: isFontBold ? "700" : "200", mr: 5 }}
					font="Lato-Regular"
				>
					{label}
				</CustomizedText>
				<Box sx={{ display: "flex", flexDirection: "column" }}>
					{totalDiscount > 0 &&
						actualValue &&
						renderAmountInCurrencyFormat({
							price: parseInt(actualValue || "0").toFixed(),
							isFontBold: false,
							isTextStrikeThrough: true,
						})}
					{renderAmountInCurrencyFormat({
						price: parseInt(value || "0").toFixed(),
						isFontBold,
					})}
				</Box>
			</PriceAndValueContainer>
		);
	};

	const renderBasicCostDetails = () => (
		<Box sx={{ width: "100%" }}>
			{renderPriceAndValue({
				label: t("list_price.basic_rate"),
				value: discountDetails?.ratePerSquareFeet,
			})}
			{renderPriceAndValue({
				label: t("list_price.floor_premium"),
				value: discountDetails?.floorPremium,
			})} {renderPriceAndValue({
				label: t("list_price.plc"),
				value: discountDetails?.plc,
			})}
			{renderPriceAndValue({
				label: t("list_price.total_discount_per_sq_ft"),
				value: discountDetails?.totalDiscount,
			})}
			{renderPriceAndValue({
				label: t("list_price.effective_rate"),
				value: discountDetails?.bookingRatePerSquareFeet,
			})}

		</Box>
	);

	const renderTaxDetails = () => (
		<Box sx={{ width: "100%" }}>
			{renderPriceAndValue({
				label: t("list_price.gst_percentage", {
					gstPercentage: discountDetails?.gstPercentage,
				}),
				value: discountDetails?.gstAmount,
			})}
		</Box>
	);

	const renderMiscellaneousDetails = () => (
		<Box sx={{ width: "100%" }}>
			{renderPriceAndValue({
				label: t("list_price.corpus_fund", {
					gstPercentage: discountDetails?.gstPercentage,
				}),
				value: discountDetails?.sinkingFund,
			})}
			{renderPriceAndValue({
				label: t("list_price.maintenance_charge", {
					gstPercentage: discountDetails?.maintenanceCharges,
				}),
				value: discountDetails?.maintenanceCharges,
			})}
			{renderPriceAndValue({
				label: t("list_price.legal_charge", {
					gstPercentage: discountDetails?.legalCharges,
				}),
				value: discountDetails?.legalCharges,
			})}
			{renderPriceAndValue({
				label: t("list_price.booking_e_katha_amount", {
					gstPercentage: discountDetails?.BookingEKhata,
				}),
				value: discountDetails?.BookingEKhata,
			})}
		</Box>
	);

	const renderAdditionalCost = () => (
		<>
			<CustomizedText fontSize={20} font="Lato-Regular">
				{t("list_price.additional_cost")}
			</CustomizedText>
			{renderPriceAndValue({
				label: t("list_price.agreement_value"),
				value: discountDetails?.bookingTotalConsideration,
				isFontBold: true,
				actualValue: discountDetails?.totalConsideration,
			})}
		</>
	);

	const renderTaxes = () => (
		<>
			<CustomizedText fontSize={20} font="Lato-Regular">
				{t("list_price.taxes")}
			</CustomizedText>
			{renderPriceAndValue({
				label: t("list_price.gst_cost"),
				value:
					discountDetails?.bookingTotalConsiderationIncludingGst,
				isFontBold: true,
				actualValue:
					discountDetails?.totalConsiderationIncludingGst,
			})}
		</>
	);

	const renderMiscellaneousCost = () => (
		<>
			<CustomizedText fontSize={20} font="Lato-Regular">
				{t("list_price.miscellaneous_cost")}
			</CustomizedText>
			{renderPriceAndValue({
				label: t("list_price.total_incl_misc_cost"),
				value:
					discountDetails?.totalEffectiveCostBeforeRoundOff?.toFixed(),
				isFontBold: true,
				actualValue: discountDetails?.totalAmount,
			})}
		</>
	);

	const renderRoundOffAmount = () =>
		renderPriceAndValue({
			label: t("list_price.round_off_amount"),
			value: discountDetails?.roundOff,
			isFontBold: true,
		});

	const renderTotalOwnershipCost = () => (
		<TotalOwnershipContainer>
			{renderPriceAndValue({
				label: t("list_price.total_ownership_cost"),
				value: discountDetails?.totalCostOfOwnership,
				isFontBold: true,
			})}
			{(!isCurrentUserCP || isProjectHead || isProjectSalesHead || isProjectSalesUser) && isEditable && (
				<CustomizedLink

					onClick={toggleRoundOffModal}
					sx={{ ml: 1, alignSelf: "end" }}
				>
					{t("list_price.roundoff")}
				</CustomizedLink>
			)}
		</TotalOwnershipContainer>
	);

	const renderTotalDiscount = () => (
		<DiscountContainer sx={totalDiscountContainerProps}>
			<CustomizedText
				fontSize={20}
				font="Lora-Bold"
				fontColor={Colors.greenColor}
				sx={{ display: "flex" }}
			>
				{t("list_price.discount_value")}
				<Box sx={{ mr: 1 }} />
				{renderAmountInCurrencyFormat({
					price: discountDetails?.discount,
					fontColor: Colors.greenColor,
					fontSize: 16,
				})}
				<CustomizedText
					fontSize={20}
					font="Lora-Bold"
					fontColor={Colors.greenColor}
					sx={{ ml: 1 }}
				>
					{`@ ${t("list_price.square_feet", {
						squareFeetValue: discountDetails?.totalDiscount,
					})}`}
				</CustomizedText>
			</CustomizedText>
		</DiscountContainer>
	);

	const renderDivider = () => (
		<CustomizedDivider width="100%" sx={{ mb: 2, mt: 0 }} />
	);

	const renderDiscountOfferedBy = () => (
		<DiscountContainer sx={{ mt: 1 }}>
			<Box sx={{ width: "100%" }}>
				{isCurrentUserCP ? (
					renderPriceAndValue({
						label: t("list_price.discount_by_channel_partner"),
						value: discountDetails?.discountByChannelPartner,
						isFontBold: true,
					})
				) : (
					<>
						{renderPriceAndValue({
							label: t("list_price.discount_by_sales_executive"),
							value: discountDetails?.discountBySalesExecutive,
							isFontBold: true,
						})}
						{renderPriceAndValue({
							label: t("list_price.discount_by_sales_lead"),
							value: discountDetails?.discountBySalesLead,
							isFontBold: true,
						})}
						{isProjectHead &&
							renderPriceAndValue({
								label: t("list_price.discount_by_sales_head"),
								value: discountDetails?.discountBySalesHead,
								isFontBold: true,
							})}
					</>
				)}
			</Box>
		</DiscountContainer>
	);

	const renderPriceDetails = () => (
		<Box sx={{ width: "100%" }}>
			<PriceContainer>
				<CustomizedText fontSize={20} font="Lato-Regular">
					{t("list_price.title")}
				</CustomizedText>
				{renderPriceAndValue({
					label: t("list_price.basic_apartment_cost"),
					value: discountDetails?.bookingBasicAmount,
					isFontBold: true,
					actualValue: discountDetails?.basicAmount,
				})}
				{renderLinkToViewDetails({ id: t("list_price.basic_apartment_cost") })}
				{isBasicCostExpandable && (
					<DiscountContainer sx={{ mt: 1 }}>
						{renderBasicCostDetails()}
					</DiscountContainer>
				)}
				{renderDivider()}
				{renderAdditionalCost()}
				{renderLinkToViewDetails({ id: t("list_price.additional_cost") })}
				<Collapse in={isAdditionalCostExpandable} timeout="auto" unmountOnExit>
					<DiscountContainer sx={{ mt: 1 }}>
						{renderAdditionalCostDetails()}
					</DiscountContainer>
				</Collapse>
				{renderDivider()}
				{renderTaxes()}
				{renderLinkToViewDetails({ id: t("list_price.taxes") })}
				<Collapse in={isTaxesCostExpandable} timeout="auto" unmountOnExit>
					<DiscountContainer sx={{ mt: 1 }}>
						{renderTaxDetails()}
					</DiscountContainer>
				</Collapse>
				{renderDivider()}
				{renderMiscellaneousCost()}
				{renderLinkToViewDetails({ id: t("list_price.miscellaneous_cost") })}
				<Collapse
					in={isMiscellaneousCostExpandable}
					timeout="auto"
					unmountOnExit
				>
					<DiscountContainer sx={{ mt: 1 }}>
						{renderMiscellaneousDetails()}
					</DiscountContainer>
				</Collapse>
				{renderDivider()}
				{Boolean(discountDetails?.roundOff) &&
					renderRoundOffAmount()}
				{renderTotalOwnershipCost()}
			</PriceContainer>
			{!(highestUserRole === USER_ROLES.ProjectSalesUser) &&
				renderDiscountOfferedBy()}
			{Boolean(totalDiscount) && renderTotalDiscount()}
		</Box>
	);

	const renderAddDiscountInput = () => (
		<DiscountOrRoundOffInput
			handleClose={validateDiscountAndClose}
			isButtonLoading={isUpdateProposalLoading}
			onClickSave={onClickSaveDiscount}
			discountInvalidMessage={discountErrorMessage}
			onChangeAmount={onChangeAmount}
			discountOrRoundOffAdded={discountAdded}
			onBlur={calculateDiscounts}
			outerLabel={t("list_price.LO_discount")}
			maxLength={DISCOUNT_MAX_LENGTH}
		/>
	);

	// CHANGE 7: Add the loading gate here.
	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
				<CircularProgress />
			</Box>
		);
	}

	// Optional: Handle case where data is fetched but empty
	if (!getProposalDetailResponse?.data?.[0]) {
		return (
			<>
				<CustomizedText fontSize={24} sx={{ mt: 2 }}>
					Proposal details not found.
				</CustomizedText>
			</>
		)
	}

	return (
		<>
			<CustomizedText fontSize={24} sx={{ mt: 2 }}>
				{t("proposal_view.calculating_price")}
			</CustomizedText>
			{renderSuperBuiltUpArea()}
			<CalculatorContainer>{renderPriceDetails()}</CalculatorContainer>
			<MaterialModal
				open={isAddDiscountPopupVisible}
				sx={materialModalProps}
				children={renderAddDiscountInput()}
			/>
		</>
	);
};

export default PriceCalculator;